import { useState, useEffect } from 'react';
import { daysBetween, formatDate, DATE } from '@koob/dates';
import { getSetting, getSettingLabel } from '@koob/utils';
import ExportPage, { PAGE_MARGIN_PX } from './ExportPage';
import ExportTourOperatorLogo from './ExportTourOperatorLogo';
import { collectTripGuests } from './tripGuests';
import { useFluidLayout } from '../layout';

export default function ExportCover({
  trip,
  countries,
  tourOperator,
  images,
  settings,
}) {
  const fluid = useFluidLayout();
  const backgroundImage = images?.[0];
  const nightsCount = daysBetween(trip?.startDate, trip?.endDate);
  const guests = collectTripGuests(trip);
  const [imageLoaded, setImageLoaded] = useState(true);

  const imageUrl =
    trip?.template?.imageUrl ?? trip?.imageUrl ?? backgroundImage?.imageUrl;

  useEffect(() => {
    setImageLoaded(true);
  }, [imageUrl]);

  const coverPageSection = getSetting(settings, 'cover-page');
  const guestsLabel = getSettingLabel(
    coverPageSection?.sections,
    'cover-page-guests',
  );
  const daysLabel = getSettingLabel(
    coverPageSection?.sections,
    'cover-page-days',
  );
  const nightsLabel = getSettingLabel(
    coverPageSection?.sections,
    'cover-page-nights',
  );
  const badgeLabel = getSettingLabel(
    coverPageSection?.sections,
    'cover-page-badge',
  );

  const generalSummarySection = getSetting(settings, 'general-summary');
  const tripSubsection = getSetting(generalSummarySection?.sections, 'trip');
  const durationLabel = getSettingLabel(
    tripSubsection?.fields,
    'trip-duration',
  );
  const startLabel = getSettingLabel(tripSubsection?.fields, 'trip-start');
  const endLabel = getSettingLabel(tripSubsection?.fields, 'trip-end');

  const hasImage = !!imageUrl && imageLoaded;

  return (
    <ExportPage trip={trip} tourOperator={tourOperator} footer={false} bleed>
      {hasImage && (
        <>
          <img
            className="absolute inset-0 h-full w-full object-cover"
            crossOrigin="anonymous"
            src={imageUrl}
            alt=""
            onError={(e) => {
              if (e.currentTarget.hasAttribute('crossorigin')) {
                e.currentTarget.removeAttribute('crossorigin');
                return;
              }
              setImageLoaded(false);
            }}
          />

          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/75" />
        </>
      )}

      <div
        className={`relative z-10 flex flex-col justify-between ${
          hasImage ? 'text-white' : 'text-[var(--td-ink)] bg-[var(--td-paper)]'
        } ${fluid ? 'flex-1' : 'h-full'}`}
        style={{ padding: `${PAGE_MARGIN_PX}px` }}
      >
        <div className="flex items-start justify-between">
          <div style={{ fontFamily: 'var(--td-font-display)' }}>
            <ExportTourOperatorLogo tourOperator={tourOperator} noImage />
          </div>

          <div
            className={`export-stamp ${
              hasImage
                ? 'export-stamp--light'
                : 'border-[var(--td-primary)] text-[var(--td-primary)]'
            }`}
          >
            {badgeLabel}
          </div>
        </div>

        <div>
          <div
            className="max-w-3xl text-8xl font-light leading-[0.95]"
            style={{ fontFamily: 'var(--td-font-display)' }}
          >
            {trip?.name}
          </div>

          {countries?.length > 0 && (
            <div
              className="mt-6 max-w-2xl text-2xl italic"
              style={{ fontFamily: 'var(--td-font-display)' }}
            >
              {countries
                .map((country) => country.name ?? country.title)
                .join(', ')}
            </div>
          )}

          <div
            className={`mt-10 grid grid-cols-4 gap-6 border-t pt-6 text-2xs uppercase tracking-widest ${
              hasImage ? 'border-white/20' : 'border-[var(--td-ink)]/20'
            }`}
          >
            <div>
              <div className={hasImage ? 'opacity-60' : 'opacity-70'}>
                {durationLabel}
              </div>
              <div className="mt-1 font-bold">
                {nightsCount + 1} {daysLabel} — {nightsCount} {nightsLabel}
              </div>
            </div>

            {!trip?.template && (
              <>
                <div>
                  <div className={hasImage ? 'opacity-60' : 'opacity-70'}>
                    {startLabel}
                  </div>
                  <div className="mt-1 font-bold">
                    {formatDate(trip?.startDate, DATE)}
                  </div>
                </div>

                <div>
                  <div className={hasImage ? 'opacity-60' : 'opacity-70'}>
                    {endLabel}
                  </div>
                  <div className="mt-1 font-bold">
                    {formatDate(trip?.endDate, DATE)}
                  </div>
                </div>
              </>
            )}

            {guests.length > 0 && (
              <div>
                <div className={hasImage ? 'opacity-60' : 'opacity-70'}>
                  {guestsLabel}
                </div>
                <div className="mt-1 font-bold normal-case tracking-normal">
                  {guests.map((guest) => (
                    <div
                      key={guest.id ?? `${guest.firstName}-${guest.lastName}`}
                    >
                      {guest.firstName} {guest.lastName}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ExportPage>
  );
}
